import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { testEvaluationService } from '../services/testEvaluationService';
import { TestResult, ReviewerComment } from '../types/testEvaluation';
import { 
  CheckCircle2, 
  XCircle, 
  ArrowLeft,
  MessageSquare,
  AlertCircle,
  Clock,
  User
} from 'lucide-react';

const TestResultDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'conversation' | 'scores' | 'comments' | 'metadata'>('conversation');
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  useEffect(() => {
    const fetchTestResult = async () => {
      if (!id) return;

      setLoading(true);
      setError(null);

      try {
        // No agentId required anymore!
        const result = await testEvaluationService.getTestResult({ id });
        
        setTestResult(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load test result');
      } finally {
        setLoading(false);
      }
    };

    fetchTestResult();
  }, [id]);

  const handleAddComment = async () => {
    if (!testResult || !newComment.trim() || !id) return;

    setSubmittingComment(true);
    try {
      // No agentId required anymore!
      const response = await testEvaluationService.addComment(id, {
        comment: newComment.trim(),
      });

      setTestResult({
        ...testResult,
        reviewerComments: [...(testResult.reviewerComments || []), response.comment],
      });

      setNewComment('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!testResult || !id) return;

    try {
      // No agentId required anymore!
      await testEvaluationService.deleteComment(id, commentId);

      setTestResult({
        ...testResult,
        reviewerComments: (testResult.reviewerComments || []).filter(c => c.id !== commentId),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete comment');
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rm-primary-600 mx-auto mb-4"></div>
          <p className="text-rm-ink-600">Loading test result...</p>
        </div>
      </div>
    );
  }

  if (error || !testResult) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-rm-error-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-rm-ink-900 mb-2">Error Loading Test Result</h2>
          <p className="text-rm-ink-600 mb-4">{error || 'Test result not found'}</p>
          <button
            onClick={() => navigate('/test-evaluation')}
            className="px-4 py-2 bg-rm-primary-600 text-white rounded-md hover:bg-rm-primary-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col space-y-6 py-6">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate('/test-evaluation')}
          className="flex items-center text-rm-primary-600 hover:text-rm-primary-700 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </button>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-rm-ink-900">
              {testResult.scenarioName || testResult.scenarioId}
            </h1>
            <div className="flex items-center space-x-4 mt-2 text-sm text-rm-ink-600">
              {testResult.category && (
                <span className="px-2 py-1 bg-rm-ink-100 text-rm-ink-700 rounded">
                  {testResult.category}
                </span>
              )}
              <span className="flex items-center">
                <Clock className="w-4 h-4 mr-1" />
                {new Date(testResult.timestamp).toLocaleString()}
              </span>
            </div>
          </div>

          <div className="text-right">
            <div className="flex items-center space-x-2 mb-2">
              {testResult.passed ? (
                <CheckCircle2 className="w-6 h-6 text-rm-success-500" />
              ) : (
                <XCircle className="w-6 h-6 text-rm-error-500" />
              )}
              <span className={`text-2xl font-bold ${testResult.passed ? 'text-rm-success-600' : 'text-rm-error-600'}`}>
                {testResult.passed ? 'Passed' : 'Failed'}
              </span>
            </div>
            <div className="text-sm text-rm-ink-600">
              Score: {(testResult.overallScore * 100).toFixed(1)}%
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-rm-ink-200">
        <div className="flex space-x-8">
          {(['conversation', 'scores', 'comments', 'metadata'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-4 px-2 font-medium text-sm transition-colors ${
                activeTab === tab
                  ? 'border-b-2 border-rm-primary-600 text-rm-primary-600'
                  : 'text-rm-ink-600 hover:text-rm-ink-900'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 bg-white border border-rm-ink-200 rounded-lg shadow-card overflow-y-auto">
        {activeTab === 'conversation' && (
          <div className="p-6">
            <h2 className="text-lg font-semibold text-rm-ink-900 mb-4">Conversation</h2>
            {testResult.conversationData?.allMessages ? (
              <div className="space-y-4">
                {testResult.conversationData.allMessages.map((message, idx) => (
                  <div
                    key={idx}
                    className={`p-4 rounded-lg ${
                      message.role === 'user'
                        ? 'bg-rm-primary-50 ml-8'
                        : 'bg-rm-ink-50 mr-8'
                    }`}
                  >
                    <div className="flex items-center space-x-2 mb-2">
                      <User className="w-4 h-4" />
                      <span className="font-medium text-sm">
                        {message.role === 'user' ? 'User' : 'Assistant'}
                      </span>
                      <span className="text-xs text-rm-ink-600">
                        {new Date(message.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                    <p className="text-rm-ink-900 whitespace-pre-wrap">{message.content}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-rm-ink-600">No conversation data available</p>
            )}
          </div>
        )}

        {activeTab === 'scores' && (
          <div className="p-6">
            <h2 className="text-lg font-semibold text-rm-ink-900 mb-4">Scores & Validation</h2>
            
            {/* Overall Score */}
            <div className="mb-6 p-4 bg-rm-ink-50 rounded-lg">
              <div className="text-sm text-rm-ink-600 mb-1">Overall Score</div>
              <div className="text-3xl font-bold text-rm-ink-900">
                {(testResult.overallScore * 100).toFixed(1)}%
              </div>
            </div>

            {/* Rule Validation */}
            {testResult.ruleValidation && (
              <div className="mb-6">
                <h3 className="font-semibold text-rm-ink-900 mb-3">Rule Validation</h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 bg-rm-ink-50 rounded">
                    <span>Status</span>
                    <span className={testResult.ruleValidation.passed ? 'text-rm-success-600' : 'text-rm-error-600'}>
                      {testResult.ruleValidation.passed ? 'Passed' : 'Failed'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-rm-ink-50 rounded">
                    <span>Score</span>
                    <span>{(testResult.ruleValidation.score * 100).toFixed(1)}%</span>
                  </div>
                  {testResult.ruleValidation.violations.length > 0 && (
                    <div className="mt-4">
                      <h4 className="font-medium text-rm-error-700 mb-2">Violations</h4>
                      <div className="space-y-2">
                        {testResult.ruleValidation.violations.map((violation, idx) => (
                          <div key={idx} className="p-3 bg-rm-error-50 border border-rm-error-200 rounded">
                            <div className="font-medium text-rm-error-900">{violation.rule}</div>
                            <div className="text-sm text-rm-error-700 mt-1">{violation.message}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* AI Evaluation */}
            {testResult.aiEvaluation?.result && (
              <div>
                <h3 className="font-semibold text-rm-ink-900 mb-3">AI Evaluation</h3>
                <div className="space-y-3">
                  {Object.entries(testResult.aiEvaluation.result.scores).map(([key, value]) => (
                    value !== undefined && (
                      <div key={key} className="flex items-center justify-between p-3 bg-rm-ink-50 rounded">
                        <span className="capitalize">{key}</span>
                        <span className="font-medium">{(value * 100).toFixed(1)}%</span>
                      </div>
                    )
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'comments' && (
          <div className="p-6">
            <h2 className="text-lg font-semibold text-rm-ink-900 mb-4">Comments</h2>

            {/* Add Comment */}
            <div className="mb-6 p-4 bg-rm-ink-50 rounded-lg">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment..."
                rows={3}
                className="w-full px-3 py-2 border border-rm-ink-300 rounded-md focus:outline-none focus:ring-2 focus:ring-rm-primary-500"
              />
              <button
                onClick={handleAddComment}
                disabled={!newComment.trim() || submittingComment}
                className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:text-gray-700 disabled:cursor-not-allowed transition-colors"
              >
                {submittingComment ? 'Adding...' : 'Add Comment'}
              </button>
            </div>

            {/* Comments List */}
            <div className="space-y-4">
              {testResult.reviewerComments && testResult.reviewerComments.length > 0 ? (
                testResult.reviewerComments.map((comment: ReviewerComment) => (
                  <div key={comment.id} className="p-4 border border-rm-ink-200 rounded-lg">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="font-medium text-rm-ink-900">{comment.displayName}</div>
                        <div className="text-sm text-rm-ink-600">{comment.userEmail}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-rm-ink-600">
                          {new Date(comment.timestamp).toLocaleString()}
                        </div>
                        {comment.userId === user?.id && (
                          <button
                            onClick={() => handleDeleteComment(comment.id)}
                            className="text-xs text-rm-error-600 hover:text-rm-error-700 mt-1"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="text-rm-ink-900 whitespace-pre-wrap">{comment.comment}</p>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-rm-ink-600">
                  <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No comments yet. Be the first to add one!</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'metadata' && (
          <div className="p-6">
            <h2 className="text-lg font-semibold text-rm-ink-900 mb-4">Metadata</h2>
            <div className="space-y-3">
              <div className="flex justify-between p-3 bg-rm-ink-50 rounded">
                <span className="text-rm-ink-600">Document ID</span>
                <span className="font-mono text-sm">{testResult.documentId}</span>
              </div>
              <div className="flex justify-between p-3 bg-rm-ink-50 rounded">
                <span className="text-rm-ink-600">Conversation ID</span>
                <span className="font-mono text-sm">{testResult.conversationId}</span>
              </div>
              <div className="flex justify-between p-3 bg-rm-ink-50 rounded">
                <span className="text-rm-ink-600">Agent ID</span>
                <span className="font-mono text-sm">{testResult.agentId}</span>
              </div>
              {testResult.turnsTotal && (
                <div className="flex justify-between p-3 bg-rm-ink-50 rounded">
                  <span className="text-rm-ink-600">Turns</span>
                  <span>{testResult.turnsCompleted}/{testResult.turnsTotal}</span>
                </div>
              )}
              {testResult.duration && (
                <div className="flex justify-between p-3 bg-rm-ink-50 rounded">
                  <span className="text-rm-ink-600">Duration</span>
                  <span>{(testResult.duration / 1000).toFixed(2)}s</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestResultDetail;
